import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { CheckCircle, Camera, X, Warning, House, Bed, Users, ClockCounterClockwise, ArrowRight, Trash, Sparkle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { compressImage } from '../lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'sporcizia', label: 'Sporcizia', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { id: 'danni', label: 'Danni', color: 'bg-rose-100 text-rose-800 border-rose-300' },
  { id: 'furto', label: 'Furto', color: 'bg-red-100 text-red-800 border-red-300' },
  { id: 'problemi', label: 'Problemi', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  { id: 'altro', label: 'Altro', color: 'bg-zinc-100 text-zinc-800 border-zinc-300' },
];

export default function StaffCleaningPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportFor, setReportFor] = useState(null); // task id
  const [reportDraft, setReportDraft] = useState({ categories: [], text: '', photos: [] });
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTasks = async () => {
    try {
      const r = await axios.get(`${API}/cleaning/public/${token}/tasks`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Link non valido o disattivato' : 'Errore di caricamento');
    } finally {
      setLoading(false);
    }
  };

  const markDone = async (taskId) => {
    if (!window.confirm('Confermi che hai completato le pulizie di questo turno?')) return;
    try {
      await axios.post(`${API}/cleaning/public/${token}/tasks/${taskId}/done`);
      toast.success('Pulizia confermata · grazie!');
      // Optimistic update
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.id === taskId ? { ...t, status: 'fatto', completed_at: new Date().toISOString() } : t),
      }));
    } catch (e) {
      toast.error('Errore conferma');
    }
  };

  const openReport = (taskId) => {
    setReportFor(taskId);
    setReportDraft({ categories: [], text: '', photos: [] });
  };

  const toggleCat = (cat) => {
    setReportDraft(d => ({
      ...d,
      categories: d.categories.includes(cat) ? d.categories.filter(c => c !== cat) : [...d.categories, cat],
    }));
  };

  const onPhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 3 - reportDraft.photos.length;
    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Massimo 3 foto totali`);
    }
    for (const f of toProcess) {
      try {
        const compressed = await compressImage(f);
        setReportDraft(d => ({ ...d, photos: [...d.photos, compressed].slice(0, 3) }));
      } catch (err) {
        toast.error('Errore compressione foto');
      }
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePhoto = (idx) => {
    setReportDraft(d => ({ ...d, photos: d.photos.filter((_, i) => i !== idx) }));
  };

  const submitReport = async () => {
    if (!reportDraft.categories.length && !reportDraft.text && !reportDraft.photos.length) {
      toast.error('Aggiungi almeno categoria, testo o foto');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/cleaning/public/${token}/tasks/${reportFor}/report`, reportDraft);
      toast.success('Segnalazione inviata');
      setData(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => t.id === reportFor ? { ...t, report_id: 'pending' } : t),
      }));
      setReportFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Caricamento…</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Link non valido</div>;

  const today = new Date().toISOString().slice(0, 10);
  // Ordina per arrivo (check-in) crescente — così lo staff si organizza fra due prenotazioni
  const sortedTasks = [...(data.tasks || [])].sort((a, b) => {
    const ci = (a.check_in || '').localeCompare(b.check_in || '');
    if (ci !== 0) return ci;
    return (a.check_out || '').localeCompare(b.check_out || '');
  });

  return (
    <div className="min-h-screen bg-zinc-50">
      <Toaster richColors position="top-center" />
      {/* Header */}
      <header className="bg-zinc-900 text-white sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-700 flex items-center justify-center">
              <Sparkle size={16} weight="bold" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-300">Staff Pulizie</p>
              <p className="text-xs font-bold truncate max-w-[200px]">{data.link_name || 'Pannello pulizie'}</p>
            </div>
          </div>
          <button onClick={fetchTasks} className="text-[10px] font-black uppercase tracking-wider text-zinc-300 hover:text-white" data-testid="refresh-btn">↻ Aggiorna</button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 pb-20">
        {sortedTasks.length === 0 && (
          <div className="bg-white border border-zinc-200 p-10 text-center">
            <CheckCircle size={32} weight="duotone" className="text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-bold text-zinc-900 mb-1">Nessun turno per ora</p>
            <p className="text-xs text-zinc-500">I prossimi check-out appariranno qui.</p>
          </div>
        )}

        <div className="space-y-3" data-testid="tasks-list">
          {sortedTasks.map(t => {
            const isDone = t.status === 'fatto';
            const isToday = t.check_out === today;
            const isPast = t.check_out < today;
            const tone = isDone ? 'border-emerald-400 bg-emerald-50' : isToday ? 'border-amber-400 bg-white' : 'border-zinc-200 bg-white';
            const guests = t.num_guests;
            return (
              <div key={t.id} className={`border-2 ${tone} p-4 sm:p-5 relative transition-all`} data-testid={`task-${t.id}`}>
                {isDone && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-emerald-600 text-white text-[9px] font-black uppercase tracking-wider px-2 py-1">
                    <CheckCircle size={11} weight="fill" /> Fatto
                  </div>
                )}
                {!isDone && isToday && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-500 text-white text-[9px] font-black uppercase tracking-wider px-2 py-1 animate-pulse">
                    OGGI
                  </div>
                )}
                {!isDone && isPast && (
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-rose-600 text-white text-[9px] font-black uppercase tracking-wider px-2 py-1">
                    IN RITARDO
                  </div>
                )}

                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center flex-shrink-0">
                    {t.room ? <Bed size={18} weight="bold" /> : <House size={18} weight="bold" />}
                  </div>
                  <div className="min-w-0 flex-1 pr-16">
                    <p className="text-base font-black text-zinc-900 truncate">{t.room?.name || t.property?.name || 'Struttura'}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{t.property?.name}{t.property?.city ? ` · ${t.property.city}` : ''}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-zinc-50 border border-zinc-200 px-3 py-2">
                    <p className="text-[9px] font-black uppercase tracking-wider text-emerald-700">Check-in</p>
                    <p className="text-sm font-bold text-zinc-900 tabular-nums">
                      {new Date(t.check_in).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    </p>
                  </div>
                  <div className="bg-zinc-50 border border-zinc-200 px-3 py-2">
                    <p className="text-[9px] font-black uppercase tracking-wider text-rose-700">Check-out</p>
                    <p className="text-sm font-bold text-zinc-900 tabular-nums">
                      {new Date(t.check_out).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    </p>
                  </div>
                  <div className="bg-zinc-50 border border-zinc-200 px-3 py-2">
                    <p className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Ospiti / Notti</p>
                    <p className="text-sm font-bold text-zinc-900 tabular-nums inline-flex items-center gap-1.5">
                      <Users size={12} weight="bold" /> {guests ?? '—'} · {Math.max(1, (new Date(t.check_out) - new Date(t.check_in)) / 86400000)} nt
                    </p>
                  </div>
                </div>

                {t.notes && (
                  <div className="mb-3 bg-amber-50 border-l-2 border-amber-400 px-3 py-2">
                    <p className="text-[9px] font-black uppercase tracking-wider text-amber-700 mb-0.5">Note dal proprietario</p>
                    <p className="text-xs text-amber-900 leading-snug">{t.notes}</p>
                  </div>
                )}

                {!isDone && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openReport(t.id)}
                      variant="outline"
                      className="flex-1 rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-10"
                      data-testid={`report-btn-${t.id}`}
                    >
                      <Warning size={14} weight="bold" className="mr-1.5" /> Segnala
                    </Button>
                    <Button
                      onClick={() => markDone(t.id)}
                      className="flex-1 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider h-10"
                      data-testid={`done-btn-${t.id}`}
                    >
                      <CheckCircle size={14} weight="bold" className="mr-1.5" /> Fatto
                    </Button>
                  </div>
                )}
                {isDone && t.completed_at && (
                  <p className="text-[10px] text-emerald-700 font-bold mt-1 inline-flex items-center gap-1">
                    <ClockCounterClockwise size={11} weight="bold" />
                    Completato {new Date(t.completed_at).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* Report dialog */}
      {reportFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="report-dialog">
          <div className="bg-white w-full sm:max-w-md sm:rounded-none max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
              <h3 className="text-sm font-black uppercase tracking-wider text-zinc-900">Nuova segnalazione</h3>
              <button onClick={() => setReportFor(null)} className="p-1 hover:bg-zinc-100"><X size={18} weight="bold" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-2">Categoria</p>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map(c => (
                    <button
                      key={c.id}
                      onClick={() => toggleCat(c.id)}
                      className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border-2 transition-all ${reportDraft.categories.includes(c.id) ? c.color : 'bg-white border-zinc-200 text-zinc-500'}`}
                      data-testid={`cat-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-2">Descrizione (opzionale)</p>
                <textarea
                  value={reportDraft.text}
                  onChange={e => setReportDraft(d => ({ ...d, text: e.target.value }))}
                  rows={3}
                  maxLength={2000}
                  placeholder="Cosa hai trovato?"
                  className="w-full border border-zinc-300 p-3 text-sm focus:outline-none focus:border-zinc-900 resize-none"
                  data-testid="report-text"
                />
              </div>

              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-2">Foto (max 3 · auto-compresse)</p>
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {reportDraft.photos.map((p, i) => (
                    <div key={i} className="relative aspect-square">
                      <img src={p} alt="" className="w-full h-full object-cover border border-zinc-200" />
                      <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-6 h-6 bg-zinc-900 text-white flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                  {reportDraft.photos.length < 3 && (
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="aspect-square border-2 border-dashed border-zinc-300 hover:border-zinc-900 flex flex-col items-center justify-center gap-1 text-zinc-500"
                      data-testid="add-photo-btn"
                    >
                      <Camera size={20} weight="bold" />
                      <span className="text-[9px] font-black uppercase">Aggiungi</span>
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={onPhotoSelect} className="hidden" data-testid="photo-input" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-zinc-200 p-4 flex gap-2">
              <Button onClick={() => setReportFor(null)} variant="outline" className="flex-1 rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider">Annulla</Button>
              <Button onClick={submitReport} disabled={submitting} className="flex-1 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider" data-testid="submit-report-btn">
                {submitting ? 'Invio…' : 'Invia segnalazione'} <ArrowRight size={12} weight="bold" className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
